import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

const StackContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
`;

const StackItem = styled(motion.div)`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${props => props.zIndex || 1};
`;

const StackContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  padding: 2rem;
  
  h2 {
    font-size: clamp(2rem, 8vw, 6rem);
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(45deg, #ffffff, #00ff00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
  }
  
  p {
    font-size: clamp(1rem, 3vw, 1.5rem);
    color: #e2e8f0;
    line-height: 1.6;
    margin-bottom: 2rem;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
  }
`;

// Separate component for each stack item to properly use hooks
const StackItemComponent = ({ item, index, totalItems, springScrollYProgress, activeIndex }) => {
  const scale = useTransform(
    springScrollYProgress,
    [index / (totalItems - 1), (index + 1) / (totalItems - 1)],
    [1, 0.8]
  );
  
  const opacity = useTransform(
    springScrollYProgress,
    [index / (totalItems - 1), (index + 1) / (totalItems - 1)],
    [1, 0.3]
  );
  
  const y = useTransform(
    springScrollYProgress,
    [index / (totalItems - 1), (index + 1) / (totalItems - 1)],
    [0, -100]
  );

  return (
    <StackItem
      style={{
        scale,
        opacity,
        y,
        zIndex: totalItems - index
      }}
      background={item.background}
      overlay={item.overlay}
      overlayOpacity={item.overlayOpacity}
    >
      <StackContent
        initial={{ opacity: 0, y: 50 }}
        animate={{ 
          opacity: activeIndex === index ? 1 : 0.5,
          y: activeIndex === index ? 0 : 20
        }}
        transition={{ duration: 0.5 }}
      >
        {item.titleComponent ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: activeIndex === index ? 1 : 0.3,
              y: activeIndex === index ? 0 : 10
            }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {item.titleComponent}
          </motion.div>
        ) : (
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: activeIndex === index ? 1 : 0.3,
              y: activeIndex === index ? 0 : 10
            }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {item.title}
          </motion.h2>
        )}
        
        {item.descriptionComponent ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: activeIndex === index ? 1 : 0.3,
              y: activeIndex === index ? 0 : 10
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {item.descriptionComponent}
          </motion.div>
        ) : (
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: activeIndex === index ? 1 : 0.3,
              y: activeIndex === index ? 0 : 10
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {item.description}
          </motion.p>
        )}
        
        {item.children && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: activeIndex === index ? 1 : 0.3,
              y: activeIndex === index ? 0 : 10
            }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {item.children}
          </motion.div>
        )}
      </StackContent>
    </StackItem>
  );
};

const StackScroll = ({ items = [] }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const springConfig = { stiffness: 300, damping: 30, restDelta: 0.001 };
  const springScrollYProgress = useSpring(scrollYProgress, springConfig);

  useEffect(() => {
    const unsubscribe = springScrollYProgress.on("change", (latest) => {
      const index = Math.floor(latest * (items.length - 1));
      setActiveIndex(index);
    });

    return () => unsubscribe();
  }, [springScrollYProgress, items.length]);

  return (
    <StackContainer ref={containerRef}>
      {items.map((item, index) => (
        <StackItemComponent
          key={index}
          item={item}
          index={index}
          totalItems={items.length}
          springScrollYProgress={springScrollYProgress}
          activeIndex={activeIndex}
        />
      ))}
    </StackContainer>
  );
};

export default StackScroll;
